import Repository from "@shared/services/api/abstracts/Repository";
import { UrlRouteMap } from "@shared/services/api/types";
import {
  MutatePasswordPayload,
  RegistrationPayload,
  LoginResponse,
  PasswordResetPayload,
  SessionLoginPayload,
} from "./serializer-types";

export type AppleLoginPayload = {
  access_token?: string;
  code?: string;
  id_token?: string;
};

export class AuthRepository extends Repository<LoginResponse> {
  public readonly repositoryName = "AuthRepository";

  public readonly endpoint = "api/auth";

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      login: "login",
      logout: "logout",
      passwordReset: "password/reset",
      loginWithGoogle: "google",
      loginWithFacebook: "facebook",
      loginWithApple: "apple",
      registration: "registration",
      mutatePassword: "password/change",
      changePassword: "password/reset/confirm",
    };
  }

  public async login(credentials: SessionLoginPayload): Promise<LoginResponse> {
    const { data } = await this.request(this.buildUrl("login"), {
      method: "POST",
      data: credentials,
      showToastOnError: false,
    });
    return data;
  }

  public async logout(): Promise<{ detail: string }> {
    const { data } = await this.request<{ detail: string }>(
      this.buildUrl("logout"),
      {
        method: "POST",
      }
    );
    return data;
  }

  public async loginWithGoogle(
    code: string,
    id_token?: string
  ): Promise<LoginResponse> {
    const { data } = await this.request(this.buildUrl("loginWithGoogle"), {
      method: "POST",
      data: { code, id_token },
    });
    return data;
  }

  public async loginWithFacebook(access_token: string): Promise<LoginResponse> {
    const { data } = await this.request(this.buildUrl("loginWithFacebook"), {
      method: "POST",
      data: { access_token },
    });
    return data;
  }

  public async changePassword(
    new_password1: string,
    new_password2: string,
    uid: string,
    token: string
  ): Promise<LoginResponse> {
    const { data } = await this.request(this.buildUrl("changePassword"), {
      method: "POST",
      data: {
        new_password1,
        new_password2,
        uid,
        token,
      },
    });
    return data;
  }

  public async loginWithApple(
    payload: AppleLoginPayload
  ): Promise<LoginResponse> {
    const { data } = await this.request(this.buildUrl("loginWithApple"), {
      method: "POST",
      data: payload,
    });
    return data;
  }

  public async passwordReset(
    payload: PasswordResetPayload
  ): Promise<{ detail: string }> {
    const { data } = await this.request<{ detail: string }>(
      this.buildUrl("passwordReset"),
      {
        method: "POST",
        data: payload,
      }
    );
    return data;
  }

  public async registration(
    payload: RegistrationPayload
  ): Promise<LoginResponse> {
    const { data } = await this.request(this.buildUrl("registration"), {
      method: "POST",
      data: payload,
    });
    return data;
  }

  public async mutatePassword(
    payload: MutatePasswordPayload
  ): Promise<{ detail: string }> {
    const { data } = await this.request<{ detail: string }>(
      this.buildUrl("mutatePassword"),
      {
        method: "POST",
        data: payload,
      }
    );
    return data;
  }
}
